/**
 * Bulk apply routing row (gateway + TLD) using SearchableSelect.
 * For Media Devoted | Created By Ciro Autiero
 */

import type { FC } from 'react';

import { SearchableSelect } from '@/components/ui';

export interface BulkRoutingGateway {
  id: number;
  name: string;
  pricePerSms?: number | null;
}

export interface PresetsModalBulkRoutingRowProps {
  gateways: BulkRoutingGateway[];
  tlds: Array<{ value: string; displayName: string }>;
  bulkRouteId: number;
  onBulkRouteIdChange: (id: number) => void;
  bulkShortLinkTld: string;
  onBulkShortLinkTldChange: (v: string) => void;
}

function gatewayLabel(g: BulkRoutingGateway): string {
  if (g.pricePerSms == null) return g.name;
  return `${g.name} ($${Number(g.pricePerSms).toFixed(3)}/SMS)`;
}

export const PresetsModalBulkRoutingRow: FC<PresetsModalBulkRoutingRowProps> = ({
  gateways,
  tlds,
  bulkRouteId,
  onBulkRouteIdChange,
  bulkShortLinkTld,
  onBulkShortLinkTldChange,
}) => {
  const gatewayOpts =
    gateways.length === 0
      ? [{ value: 0, label: 'No gateways' }]
      : gateways.map((g) => ({ value: g.id, label: gatewayLabel(g) }));

  const tldOpts =
    tlds.length === 0
      ? [{ value: '', label: 'No TLDs' }]
      : tlds.map((t) => ({ value: t.value, label: t.displayName || t.value }));

  return (
    <div className="flex flex-wrap items-end gap-3 rounded-md border border-border/60 bg-background/40 p-2">
      <p className="w-full text-xs text-muted-foreground">
        Rule-based presets need a gateway and short-link TLD when they are not stored on the preset
        template.
      </p>
      <div className="flex min-w-[140px] flex-1 flex-col gap-1">
        <span className="text-[10px] font-medium uppercase text-muted-foreground">Gateway</span>
        <SearchableSelect
          className="h-9 rounded-md"
          options={gatewayOpts}
          value={bulkRouteId}
          onChange={(v) => onBulkRouteIdChange(Number(v))}
          placeholder="Select gateway"
          disabled={gateways.length === 0}
        />
      </div>
      <div className="flex min-w-[140px] flex-1 flex-col gap-1">
        <span className="text-[10px] font-medium uppercase text-muted-foreground">Short link TLD</span>
        <SearchableSelect
          className="h-9 rounded-md"
          options={tldOpts}
          value={bulkShortLinkTld}
          onChange={(v) => onBulkShortLinkTldChange(String(v))}
          placeholder="Select TLD"
          disabled={tlds.length === 0}
        />
      </div>
    </div>
  );
};
